/**
 * The blog page model (CO 536, CO 540).
 *
 * Posts come from the federation gateway's public `/api/blog`, backed by the
 * marketing platform's blog lane (`marketing_posts`) on the tenant the gateway
 * names in BLOG_TENANT_ID. There is no bundled fallback: the SPA and the API
 * are served by the same process, so an unreachable API is shown as such
 * rather than masked by stale content (the CO 032-002 `.md` pipeline this
 * replaces drifted 8 of 17 posts away from the live set).
 *
 * CO 540 — the blog is a CATALOG. A post may belong to a series (an ordered
 * folder: key, title, part) and may name related posts. The list page renders
 * `/api/blog/series` — series folders with their parts in reading order, then
 * the standalone posts — which is the same projection the marketing platform's
 * `marketing_post_catalog` tool serves an agent. Sort and category controls
 * ride the same query vocabulary the tool takes. Nothing is computed here that
 * the lane does not already hold: the page is a projection.
 */

import type { ApiBlogPost, ApiBlogSeriesFolder } from "../../services/api";

/** Author of a post — name plus role. */
export interface BlogAuthor {
  name: string;
  role: string;
}

/** CO 540 — membership in an ordered folder. */
export interface BlogSeriesRef {
  key: string;
  title: string;
  part: number;
}

/** A post as the pages read it: API metadata plus the markdown `body`. */
export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  author: BlogAuthor;
  publishedAt: string; // ISO date (YYYY-MM-DD)
  category: string;
  tags: string[];
  imageUrl?: string;
  readingTime?: number;
  /** CO 540 — the folder this post sits in, or null. */
  series: BlogSeriesRef | null;
  /** CO 540 — slugs of related posts on the same blog (may be empty). */
  related: string[];
  /** Raw markdown body — empty on the list page (the list endpoint omits it). */
  body: string;
}

/** CO 540 — one folder of the catalog. */
export interface BlogSeriesFolder {
  key: string;
  title: string;
  count: number;
  posts: BlogPost[];
}

export interface BlogCatalog {
  series: BlogSeriesFolder[];
  standalone: BlogPost[];
}

/**
 * CO 540 — the sort words a reader can pick on the list page. `reading_order`
 * is deliberately absent here: it is the series page's order, not a list sort.
 */
export const BLOG_SORTS = [
  { value: "sort_order", label: "Curated order" },
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "most_read", label: "Most read" },
] as const;
export type BlogSort = (typeof BLOG_SORTS)[number]["value"];
export const DEFAULT_BLOG_SORT: BlogSort = "sort_order";

export function isBlogSort(v: string | null | undefined): v is BlogSort {
  return BLOG_SORTS.some((s) => s.value === v);
}

/** Hero copy for the blog index. */
export const blogHero = {
  tagline: "ComOS Blog",
  headline: "Notes from the operating system you run your business on",
  subheadline:
    "Field notes on agentic commerce, multiplatform architecture, and building a verifiable operating system for commerce.",
} as const;

/** The one honest line shown when the blog API cannot be reached. */
export const BLOG_UNAVAILABLE_MESSAGE =
  "The blog is temporarily unavailable. Please try again in a few minutes.";

/** CO 540 — shown when a series key resolves to nothing. */
export const BLOG_SERIES_NOT_FOUND_MESSAGE =
  "We couldn't find a series at that address.";

const dateFmt = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "long",
  day: "numeric",
});

export function formatDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : dateFmt.format(d);
}

/**
 * Map an API post onto the shape the pages use. The list endpoint omits
 * `content`, so `body` is empty for list rows; the post page fetches the full
 * body per-slug.
 */
export function mapApiPost(p: ApiBlogPost): BlogPost {
  return {
    slug: p.slug,
    title: p.title,
    excerpt: p.excerpt,
    author: p.author,
    publishedAt: (p.publishedAt || p.createdAt || "").slice(0, 10),
    category: p.category,
    tags: p.tags ?? [],
    imageUrl: p.featuredImage,
    readingTime: p.readingTime,
    series: p.series ? { key: p.series.key, title: p.series.title, part: p.series.part } : null,
    related: p.related ?? [],
    body: p.content ?? "",
  };
}

export function mapApiFolder(f: ApiBlogSeriesFolder): BlogSeriesFolder {
  return { key: f.key, title: f.title, count: f.count, posts: f.posts.map(mapApiPost) };
}
